import { useEffect, useRef, useState } from 'react';

import { OperationsFilterParams } from 'api/types/data/operation';
import { OrderStatusType } from 'api/types/entity/order';
import { DateFilter } from 'components/Filters/DateFilter';
import { SelectFilter } from 'components/Filters/SelectFilter';
import { useTranslation } from 'libs/i18n';

import { TradersFilterFeature } from './TradersFilterFeature';

interface Props {
  filter: OperationsFilterParams;
  onApply: (filter: OperationsFilterParams) => void;
}

export const OperationsFilters = ({ filter, onApply }: Props) => {
  const { t } = useTranslation();

  const isMounted = useRef(false);

  const [orderStatuses, setOrderStatuses] = useState<OrderStatusType[]>(
    filter.orderStatuses ?? []
  );
  const [selectedTraders, setSelectedTraders] = useState<string[]>(
    filter.traderIds ?? []
  );
  const [executedAtTimestamps, setExecutedAtTimestamps] = useState<number[]>(
    filter.executionTimestamps ?? []
  );
  const [orderEndTimestamps, setOrderEndTimestamps] = useState<number[]>(
    filter.orderEndTimestamps ?? []
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    onApply({
      orderStatuses: orderStatuses.length ? orderStatuses : undefined,
      traderIds: selectedTraders.length ? selectedTraders : undefined,
      executionTimestamps: executedAtTimestamps.length
        ? executedAtTimestamps
        : undefined,
      orderEndTimestamps: orderEndTimestamps.length
        ? orderEndTimestamps
        : undefined,
    });
  }, [
    executedAtTimestamps,
    onApply,
    orderEndTimestamps,
    orderStatuses,
    selectedTraders,
  ]);

  return (
    <>
      <SelectFilter
        activeValues={orderStatuses}
        options={Object.values(OrderStatusType).map((status) => ({
          label: t(`order.status.${status}`),
          value: status,
        }))}
        title={t('operation.filters.orderStatus')}
        onChange={setOrderStatuses}
      />
      <DateFilter
        activeTimestampValues={orderEndTimestamps}
        title={t('operation.filters.orderEndDate')}
        onChange={setOrderEndTimestamps}
      />
      <DateFilter
        disableFuture
        activeTimestampValues={executedAtTimestamps}
        title={t('operation.filters.executedAt')}
        onChange={setExecutedAtTimestamps}
      />
      <TradersFilterFeature
        selectedTraders={selectedTraders}
        setSelectedTraders={setSelectedTraders}
      />
    </>
  );
};
